import React, { useMemo } from 'react';
import { RootState, useAppSelector, useAppDispatch } from 'src/store';
import { useTheme } from 'styled-components';
import { Flex, Text, Card, Button } from 'src/components/common';
import { getAppointDate } from 'src/utils/date-parser';
import { useDisclosure } from 'src/hooks/useDisclosure';
import { ToastNotifications } from 'src/utils/toast-notifications';
import { appointmentsAPI } from 'src/api/appointments/ApiRequests';
import { refreshCurrentUser } from 'src/store/slices/authSlice';
import { parseError } from 'src/utils/error-parser';
import { setSelectedSession } from 'src/store/slices/sessionSlice';
import { Appointment } from 'src/types/Appointment';
import { useTranslation } from 'react-i18next';
import { PaymentModal } from 'src/components/shared/payment-modal/PaymentModal';
import { getTherapistPriceBAM } from 'src/utils/price-calculator';
import { CompanyVoucher } from 'src/types/CompanyVoucher';
import { logEvent } from '../../utils/logger';

interface UnpaidSessionsProps {
  cancelSession: (session: Appointment) => void;
}

export const UnpaidSessions = ({ cancelSession }: UnpaidSessionsProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const { t } = useTranslation();
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { session } = useAppSelector((state: RootState) => state.session);

  const { isOpen, close, open } = useDisclosure(false);

  const unpaidSessions = useMemo(() => {
    return user?.appointments?.filter((session) => !session.paid)?.sort((a, b) => a.date - b.date) ?? [];
  }, [user]);

  const hasPackage = useMemo(() => {
    return user?.package?.sessions >= 1;
  }, [user]);

  const generateOrderNumber = useMemo(() => {
    return `PSHT-${new Date().getTime()}-${session?.date}`;
  }, [session]);

  if (unpaidSessions.length === 0) return null;

  const closePayForAppointment = () => {
    dispatch(setSelectedSession(null));
    close();
  };

  const handleBookAppointment = async (orderNumber: string, panToken?: string, voucher?: CompanyVoucher) => {
    logEvent('Unpaid session: book appintment');

    const data = {
      sessionId: session.id,
      orderNumber: orderNumber,
      panToken: panToken,
      voucherId: voucher ? voucher.id : null,
    };

    try {
      logEvent(JSON.stringify(data));

      const response = await appointmentsAPI.bookPaidAppointment(data);
      if (response.success) {
        ToastNotifications.success(t('msg_session_paid'), 'bookPaidAppointment');

        await dispatch(refreshCurrentUser());

        setTimeout(() => {
          closePayForAppointment();
        }, 200);
      } else {
        ToastNotifications.error(response.error, 'bookPaidAppointment');
      }
    } catch (error) {
      ToastNotifications.error(parseError(error), 'bookPaidAppointment');
    }
  };

  const bookWithVoucher = async (voucher: string) => {
    logEvent('Unpaid session: book with voucher');

    try {
      const response = await appointmentsAPI.payAppointmentWithVoucher({
        appointmentId: session.id,
        voucher: voucher,
      });
      if (response.success) {
        ToastNotifications.success(t('msg_session_paid'), 'payAppointmentWithVoucher');
        await dispatch(refreshCurrentUser());

        setTimeout(() => {
          closePayForAppointment();
        }, 200);
      } else {
        ToastNotifications.error(response.error, 'payAppointmentWithVoucher');
      }
    } catch (error) {
      ToastNotifications.error(parseError(error), 'payAppointmentWithVoucher');
    }
  };

  const bookFromPackage = async () => {
    logEvent('Unpaid session: book from package');

    try {
      const response = await appointmentsAPI.bookUnpaidAppointmentClient({
        sessionId: session?.id,
      });
      if (response.success) {
        ToastNotifications.success(t('msg_session_paid'), 'bookFromPackage');
        await dispatch(refreshCurrentUser());
      } else {
        ToastNotifications.error(response.error, 'bookFromPackage');
      }
    } catch (error) {
      ToastNotifications.error(parseError(error), 'bookFromPackage');
    }
  };

  const payForAppointment = () => {
    if (hasPackage) {
      bookFromPackage();
    } else {
      open();
    }
  };

  const calculatePrice = useMemo(() => {
    return getTherapistPriceBAM(session?.therapist?.price);
  }, [session]);

  return (
    <Flex flexDirection={'column'} width={'100%'} gap={'16px'} overflow={'scroll'}>
      <Text variant={'heading6'} text={t('sessions_9')} />

      <Card width={'100%'} borderRadius={'20px'}>
        <Flex flexDirection={'column'} width={'100%'} gap={'16px'}>
          {unpaidSessions?.map((appointment, index) => (
            <Flex key={index} flexDirection={'column'} width={'100%'} gap={'16px'}>
              {appointment.therapist.id != user.id ? (
                <Flex width={'100%'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} gap={'15px'}>
                  <Text
                    variant={'paragraph1'}
                    text={`${t('sessions_4')} ${appointment.therapist.username}, ${getAppointDate(appointment.date)}`}
                  />
                  <Flex flexDirection="row" gap="10px">
                    <Button
                      type="button"
                      variant={'primaryDefault'}
                      text={t('btn_pay_for_session')}
                      onClick={() => {
                        dispatch(setSelectedSession(appointment));
                        payForAppointment();
                      }}
                    />
                    <Button
                      type="button"
                      variant={'secondaryRedDefault'}
                      text={t('btn_cancel')}
                      onClick={() => cancelSession(appointment)}
                    />
                  </Flex>
                </Flex>
              ) : (
                <Text
                  variant={'paragraph1'}
                  text={`${t('sessions_4')} ${appointment.client.username}, ${getAppointDate(appointment.date)}`}
                />
              )}

              <Flex width={'100%'} height={'0px'} border={`0.05px solid ${theme.colors.border.darker}`} />
            </Flex>
          ))}
        </Flex>
      </Card>

      <PaymentModal
        isOpen={isOpen}
        amount={calculatePrice}
        orderNumber={generateOrderNumber}
        enableVoucher={true}
        onClose={closePayForAppointment}
        paymentSuccessful={handleBookAppointment}
        payWithVoucher={bookWithVoucher}
      />
    </Flex>
  );
};
